/**
 * API Service Layer
 *
 * Single axios instance with:
 *   - baseURL from env (REACT_APP_BACKEND_URL) or window.location.origin
 *   - Request interceptor: attach JWT from cookies
 *   - Response interceptor: handle 401 -> redirect to login
 *   - Helper functions: get, post, patch, del
 */

import axios from "axios";
import Cookies from "js-cookie";
import { attachSystemLogInterceptors, logClientError } from "./systemLogs";
import { queryClient } from "./queryClient";

/* ── Base URL ──────────────────────────────────────────────────────── */
const baseURL = (() => {
  const raw = process.env.REACT_APP_BACKEND_URL;
  if (raw && raw.trim() !== "") {
    return raw.replace(/\/$/, "");
  }
  return window.location.origin;
})();

/* ── Create axios instance ─────────────────────────────────────────── */
const api = axios.create({
  baseURL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30s timeout
});
attachSystemLogInterceptors(api);

/* ── Request interceptor: attach JWT from cookies ──────────────────── */
api.interceptors.request.use(
  (config) => {
    const tokenRaw = Cookies.get("token");
    if (tokenRaw) {
      try {
        // Token may be stored as JSON string
        const token = JSON.parse(tokenRaw);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch {
        // If not JSON, use raw value
        if (tokenRaw) {
          config.headers.Authorization = `Bearer ${tokenRaw}`;
        }
      }
    }
    return config;
  },
  (error) => {
    logClientError("services.api.request", error);
    return Promise.reject(error);
  }
);

/* ── Session teardown ──────────────────────────────────────────────── */
/** Client-side auth cookies (the httpOnly `jwt` cookie is the server's). */
export const AUTH_COOKIE_NAMES = ["token", "user"];
const LOGOUT_REQUEST_TIMEOUT_MS = 5000;

/**
 * Forget everything this tab holds about the signed-in user: the global
 * bearer header that raw-axios callers rely on (set at login in index.js /
 * userSlice), the client-side auth cookies, and the React Query cache so the
 * next login never shows the previous user's notifications, feed or
 * conversations. Safe to call more than once.
 */
export function clearClientSession() {
  try {
    delete axios.defaults.headers.common.Authorization;
  } catch (_) {}
  try {
    delete api.defaults.headers.common.Authorization;
  } catch (_) {}
  AUTH_COOKIE_NAMES.forEach((name) => {
    try {
      Cookies.remove(name);
    } catch (_) {}
  });
  try {
    queryClient.clear();
  } catch (_) {}
}

/**
 * Full logout. Order matters: the server call goes first because the request
 * interceptor still needs the token cookie to authenticate it (the server
 * then expires its httpOnly `jwt` cookie); errors are ignored so the local
 * teardown also happens offline. The realtime socket is closed here because
 * the app shell does not unmount on logout (it renders <Navigate> instead).
 *
 * @param {object}  [options]
 * @param {object}  [options.socket]        - socket.io client to disconnect
 * @param {boolean} [options.notifyServer]  - false for an already-invalid session
 */
export async function logoutSession({ socket, notifyServer = true } = {}) {
  if (notifyServer) {
    try {
      await api.post(
        "/api/v1/auth/logout",
        {},
        {
          timeout: LOGOUT_REQUEST_TIMEOUT_MS,
          // A 401 here must not re-enter handleUnauthorized (hard redirect).
          skipUnauthorizedHandling: true,
        }
      );
    } catch (_) {}
  }
  try {
    socket?.disconnect?.();
  } catch (_) {}
  clearClientSession();
}

/* ── Response interceptor: handle 401 ──────────────────────────────── */
/**
 * Drop the browser session and send the user to the login page. Shared by
 * the 401 interceptor below, by realtime auth failures (SOCKET_AUTH_*
 * connect errors, the server's `auth:expired` signal) and, via
 * logoutSession(), by the manual Logout button, so every "you are no longer
 * signed in" path ends in the same place.
 */
export function handleUnauthorized() {
  clearClientSession();

  // Redirect to login (avoid redirect loops)
  const currentPath = window.location.pathname;
  if (currentPath !== "/login" && currentPath !== "/") {
    window.location.href = "/login";
  }
}

api.interceptors.response.use(
  (response) => response,
  (error) => {
    logClientError("services.api.response", error);
    if (
      error.response &&
      error.response.status === 401 &&
      !error.config?.skipUnauthorizedHandling
    ) {
      handleUnauthorized();
    }
    return Promise.reject(error);
  }
);

/* ── Helper functions ──────────────────────────────────────────────── */

/**
 * GET request.
 * @param {string} url     - Endpoint path (e.g. "/api/v1/users/me")
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function get(url, config = {}) {
  const response = await api.get(url, config);
  return response.data;
}

/**
 * POST request.
 * @param {string} url      - Endpoint path
 * @param {object} [data]   - Request body
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function post(url, data = {}, config = {}) {
  const response = await api.post(url, data, config);
  return response.data;
}

/**
 * PATCH request.
 * @param {string} url      - Endpoint path
 * @param {object} [data]   - Request body
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function patch(url, data = {}, config = {}) {
  const response = await api.patch(url, data, config);
  return response.data;
}

/**
 * DELETE request.
 * @param {string} url      - Endpoint path
 * @param {object} [config] - Additional axios config
 * @returns {Promise<any>}  - Response data
 */
export async function del(url, config = {}) {
  const response = await api.delete(url, config);
  return response.data;
}

/**
 * Upload file via POST with FormData.
 * @param {string}   url       - Endpoint path
 * @param {FormData} formData  - FormData object
 * @param {function} [onProgress] - Upload progress callback (percentage 0-100)
 * @param {object}   [config]  - Additional axios config
 * @returns {Promise<any>}     - Response data
 */
export async function upload(url, formData, onProgress, config = {}) {
  const response = await api.post(url, formData, {
    ...config,
    headers: {
      ...config.headers,
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const pct = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          onProgress(pct);
        }
      : undefined,
  });
  return response.data;
}

/* ── Export the raw instance for advanced use cases ────────────────── */
export default api;
